//@ts-nocheck
import { routingMixin } from "@jack-henry/web-component-router";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import {
  fixedEncodeURIComponent,
  getRequest,
  putRequest,
} from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { AppRouters } from "../../utils/app-router";
import {
  decryptValue,
  showToastMessage,
  stringComparison,
} from "../../utils/app-utils";
import { StatusConstants } from "../../utils/apiStatusConstants";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { EditBusinessUnitType } from "../../types/BusinessUnits/EditBusinessUnitType";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/cards/jha-card/jha-card";
import "@banno/jha-wc/src/forms/jha-form-text-input/jha-form-text-input";
import "@banno/jha-wc/src/forms/jha-form-textarea-input/jha-form-textarea-input";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import Cookies from "js-cookie";
import { UserTypes } from "../../utils/constants";
import { isSessionBusinessUnitAllowed } from "../../utils/business-unit-session";

@customElement("edit-business-unit")
export class EditBusinessUnit extends routingMixin(LitElement) {
  constructor() {
    super();
  }
  static get is() {
    return "edit-business-unit";
  }

  @property({ type: Boolean }) isEditable = /readonly/i.test(
    decryptValue(Cookies.get("userRole") || "")
  );
  @property({ reflect: true, attribute: "business-unit-id" }) businessUnitId =
    "";
  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) dataNeedsSaving: boolean = false;

  isReadOnlyUser = /readonly/i.test(decryptValue(Cookies.get("userRole")!))
    ? true
    : false;

  @property({ type: Object }) newFormData: EditBusinessUnitType = {
    businessUnitId: "",
    businessUnitName: "",
    description: "",
    status: "",
    updatedBy: decryptValue(Cookies.get("userId") || ""),
    eTag: "",
  };

  @property({ type: Object }) originalFormData: EditBusinessUnitType = {
    businessUnitId: "",
    businessUnitName: "",
    description: "",
    status: "",
    updatedBy: decryptValue(Cookies.get("userId") || ""),
    eTag: "",
  };

  @property({ type: String })
  loggedUserBu = Cookies.get("businessUnit");

  @property({ type: String })
  loggedUserType = decryptValue(Cookies.get("userType") as string);

  static readonly styles = css`
    #btnCon {
      display: flex;
      flex-direction: row;
      gap: 15px;
    }
    #formRow {
      display: inline-block;
      flex-direction: column;
      max-width: 300px;
      margin-top: 20px;
      margin-bottom: 20px;
    }
    #editBusinessUnit {
      padding: 24px;
      height: -webkit-fill-available;
      display: flex;
      flex-direction: column;
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }
    h2 {
      margin-bottom: 18px;
    }

    jha-card {
      padding: 24px;
      width: -webkit-fill-available;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    jha-form-textarea-input {
      max-width: 410px;
    }
    jha-form-text-input {
      max-width: 410px;
    }
    textarea {
      max-width: 410px;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    const url =
      Apis.GET_BUSINESSUNIT +
      `/` +
      fixedEncodeURIComponent(this.businessUnitId);
    const result = await getRequest<EditBusinessUnitType>(url);

    if (
      result instanceof Response &&
      result.status === StatusConstants.UNAUTHORIZED_USER_CODE
    ) {
      showToastMessage(
        "You do not have permission to view this Business Unit.",
        "danger"
      );
      AppRouters.go("/business-units");
      return;
    }

    if (result instanceof Response) {
      return;
    }

    const data = result as EditBusinessUnitType;

    if (
      this.loggedUserType?.toLowerCase() === UserTypes.businessadmin &&
      !isSessionBusinessUnitAllowed(data)
    ) {
      showToastMessage(
        "You do not have permission to view this Business Unit.",
        "danger"
      );
      AppRouters.go("/business-units");
      return;
    }

    this.originalFormData = data;
    this.newFormData = { ...this.originalFormData };
    this.dataNeedsSaving = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  async onCancelClick() {
    const leave = () => {
      AppRouters.go("/business-units");
    };

    // isEditable is really "is read-only" here. dataNeedsSaving is the same
    // signal that disables Update until something actually changed.
    if (this.isEditable || !this.dataNeedsSaving) {
      leave();
      return;
    }

    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      if (result === "confirm") {
        leave();
        return;
      }
    } finally {
      if (this.isConnected) {
        this.clearButtonLoading();
      }
    }
  }

  /**
   * Handles changes to the business unit data inputs.
   * Validates the inputs and enables/disables the update button accordingly.
   */
  onBusinessUnitDataChange() {
    if (this.apiLoading) return;

    const root = this.shadowRoot;
    if (!root) return;

    const businessUnitNameInput = root.querySelector(
      "jha-form-text-input[data-auto-id='businessUnitNameInput']"
    ) as HTMLInputElement | null;
    const businessUnitDescriptionInput = root.querySelector(
      "jha-form-textarea-input[data-auto-id='businessUnitDescriptionInput']"
    ) as HTMLTextAreaElement | null;
    const updateBusinessUnitButton = root.querySelector(
      "jha-button[data-auto-id='updateBusinessUnitButton']"
    ) as HTMLButtonElement | null;
    const businessUnitActiveInput = root.querySelector(
      "jha-form-switch[data-auto-id='businessUnitActiveInput']"
    ) as HTMLInputElement | null;

    if (
      !businessUnitNameInput ||
      !businessUnitDescriptionInput ||
      !updateBusinessUnitButton ||
      !businessUnitActiveInput
    )
      return;

    const buname = businessUnitNameInput.value.trim();
    const description = businessUnitDescriptionInput.value.trim();
    const status = businessUnitActiveInput.checked ? "active" : "inactive";

    this.newFormData = {
      ...this.newFormData,
      businessUnitName: buname,
      description,
      status,
    };

    const statusTooltip = root.querySelector(
      `[data-auto-id='switchTooltip']`
    ) as HTMLElement | null;
    const statusMessage = root.querySelector(
      `[data-auto-id='businessUnitStatusMessage']`
    ) as HTMLElement | null;

    // Determine previous and current status
    const prevStatus = this.originalFormData?.status?.toLowerCase();
    const currStatus = status.toLowerCase();

    // Show message only when status is being changed from active to inactive
    if (statusMessage) {
      if (prevStatus === "active" && currStatus === "inactive") {
        statusMessage.style.display = "table-cell";
      } else {
        statusMessage.style.display = "none";
      }
    }

    if (statusTooltip) {
      if (currStatus === "active") {
        statusTooltip.setAttribute("text", "Business Unit is Active");
      } else {
        statusTooltip.setAttribute("text", "Business Unit is Inactive");
      }
    }

    const inputsAreValid =
      this.isValidString(buname) && this.isValidString(description);
    const dataUnchanged = this.compareOriginalIsSameAsNew(this.newFormData);

    if (inputsAreValid && dataUnchanged) {
      updateBusinessUnitButton.setAttribute("disabled", "");
    } else {
      updateBusinessUnitButton.removeAttribute("disabled");
    }

    this.dataNeedsSaving = !dataUnchanged;
  }

  isValidString(value: unknown): boolean {
    return typeof value === "string" && value.trim().length > 0;
  }

  /**
   * Compares the original form data with the new form data to check if they are the same.
   * Trims both original and new values before comparing to ignore leading/trailing spaces.
   * This is used to determine if the form data has changed to enable/disable the update button.
   * @param newFormData - The new form data to compare against the original.
   * @returns {boolean} - Returns true if all trimmed values are the same, false otherwise.
   */
  compareOriginalIsSameAsNew(newFormData: EditBusinessUnitType): boolean {
    for (const key in this.originalFormData) {
      const typedKey = key as keyof EditBusinessUnitType;
      const originalValue = this.originalFormData[typedKey];
      const newValue = newFormData[typedKey];

      if (originalValue === undefined || newValue === undefined) {
        continue;
      }
      if (typeof originalValue === "string" && typeof newValue === "string") {
        if (originalValue.trim() !== newValue.trim()) {
          return false;
        }
      } else if (originalValue !== newValue) {
        return false;
      }
    }
    return true;
  }

  async onUpdateBusinessUnitClick() {
    this.apiLoading = true;
    const trimmedFormData = this.trimFormDataValues(this.newFormData);
    this.newFormData = trimmedFormData;

    if (this.validateFormData(this.newFormData)) {
      const reqParams = this.newFormData;
      const formDataSubmit = await putRequest<EditBusinessUnitType>(
        Apis.UPDATE_BUSINESSUNIT +
        `/` +
        fixedEncodeURIComponent(this.businessUnitId),
        this.newFormData.eTag,
        reqParams
      );
      if (formDataSubmit?.messageStatus === null) {
        this.clearButtonLoading();
        this.apiLoading = false;
        showToastMessage("Business unit was updated successfully.", "success");
        if (this.loggedUserType?.toLowerCase() === UserTypes.businessadmin) {
          Cookies.set("businessUnit", this.newFormData.businessUnitName || "");
        }

        setTimeout(() => {
          this.clearForm();
        }, 500);
        AppRouters.go("/business-units");
      } else if (formDataSubmit.details.length > 0) {
        this.apiLoading = false;
        this.clearButtonLoading();
        const errMsg = getErrorMessage(formDataSubmit.details);
        showToastMessage(errMsg, "danger");
      } else {
        this.apiLoading = false;
        this.clearButtonLoading();
        showToastMessage("Something went wrong, please try again.", "danger");
      }
    }
    this.apiLoading = false;
  }

  clearForm() {
    const businessUnitNameInput = document
      .querySelector("edit-business-unit")
      ?.shadowRoot?.querySelector(
        "jha-form-text-input[data-auto-id='businessUnitNameInput']"
      ) as HTMLInputElement;
    const businessUnitDescriptionInput = document
      .querySelector("edit-business-unit")
      ?.shadowRoot?.querySelector(
        "jha-form-textarea-input[data-auto-id='businessUnitDescriptionInput']"
      ) as HTMLTextAreaElement;

    if (businessUnitNameInput) {
      businessUnitNameInput.value = "";
    }

    if (businessUnitDescriptionInput) {
      businessUnitDescriptionInput.value = "";
    }
  }

  clearButtonLoading() {
    const updateBusinessUnitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='updateBusinessUnitButton']"
    ) as HTMLButtonElement | null;
    updateBusinessUnitButton?.removeAttribute("isLoading");

    const cancelEditBusinessUnitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='cancelEditBusinessUnitButton']"
    ) as HTMLButtonElement | null;
    cancelEditBusinessUnitButton?.removeAttribute("isLoading");
  }

  validateFormData(formData: EditBusinessUnitType): boolean {
    if (formData?.businessUnitName === "") {
      showToastMessage("Business Unit Name is a required field.", "danger");
      return false;
    }
    if (formData.description === "") {
      showToastMessage("Description is a required field.", "danger");
      return false;
    }
    if (formData.status === "") {
      showToastMessage("Please set status to active or inactive.", "danger");
      return false;
    }
    return true;
  }

  trimFormDataValues(formData: EditBusinessUnitType): EditBusinessUnitType {
    const trimmedFormData: EditBusinessUnitType = {
      businessUnitId: formData.businessUnitId.trim(),
      businessUnitName: formData?.businessUnitName
        ? formData?.businessUnitName.trim()
        : "",
      description: formData.description ? formData.description.trim() : "",
      status: formData.status ? formData.status.trim() : "",
      updatedBy: formData.updatedBy
        ? formData.updatedBy.trim()
        : decryptValue(Cookies.get("userId") || ""),
      eTag: formData.eTag,
    };
    return trimmedFormData;
  }

  render() {
    return html`
      <div id="editBusinessUnit">
        <h1>Edit Business Unit</h1>
        <jha-card>
          <div>
            <h2>Business Unit Information</h2>

            <jha-tooltip
              right
              text="Enter Business Unit Name. (Maximum 256 characters)"
            >
              <jha-form-text-input
                data-auto-id="businessUnitNameInput"
                label="Business Unit Name"
                required
                warning="Business Unit Name is a required field."
                outline
                minlength="1"
                maxlength="256"
                @change=${this.onBusinessUnitDataChange}
                .value=${this.newFormData.businessUnitName ?? ''}
              ></jha-form-text-input>
            </jha-tooltip>
          </div>

          <div id="formRow">
            <jha-tooltip
              right
              text=${this.newFormData.status?.toLowerCase() === "active"
        ? "Click to make Business Unit Inactive"
        : "Click to make Business Unit Active"}
            >
              <jha-form-switch
                data-auto-id="businessUnitActiveInput"
                ?disabled=${this.isReadOnlyUser || this.originalFormData?.businessUnitName?.toLowerCase() === this.loggedUserBu?.toLowerCase()}
                ?checked=${this.newFormData.status?.toLowerCase() === "active"
        ? true
        : false}
                @change=${() => this.onBusinessUnitDataChange()}
              >
                <span slot="left-label">Status</span>
              </jha-form-switch>
              <jha-message
                data-auto-id="businessUnitStatusMessage"
                style="display: none; padding: 5px;"
                danger
                role="alert"
                type="info"
              >
                All users and workflows associated with this Business Unit will
                also be disabled.
              </jha-message>
            </jha-tooltip>
          </div>

          <jha-tooltip
            left-bottom
            text="Enter Business Unit Description. (Maximum 256 characters)"
          >
            <div style="max-width: 410px;">
              <jha-form-textarea-input
                data-auto-id="businessUnitDescriptionInput"
                label="Description"
                role="textbox"
                minlength="1"
                maxlength="256"
                required
                warning="Business Unit Description is a required field."
                type="text"
                @change=${this.onBusinessUnitDataChange}
                .value=${this.newFormData.description}
              >
              </jha-form-textarea-input>
            </div>
          </jha-tooltip>

          <div style="flex:1;"></div>
          <div id="btnCon">
            <jha-tooltip top text="Click to Update Business Unit">
              <jha-button
                data-auto-id="updateBusinessUnitButton"
                disabled
                type="button"
                large
                @click=${this.onUpdateBusinessUnitClick}
              >
                Update Business Unit
              </jha-button>
            </jha-tooltip>

            <jha-tooltip top text="Click to Cancel Editing Business Unit">
              <jha-button
                data-auto-id="cancelEditBusinessUnitButton"
                type="button"
                tertiary
                large
                @click=${this.onCancelClick}
              >
                Cancel
              </jha-button>
            </jha-tooltip>
          </div>
        </jha-card>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "edit-business-unit": EditBusinessUnit;
  }
}
